<template>
    <v-menu>
        <template v-slot:activator="{ props }">
            <v-btn v-bind="props" rounded="pill" variant="flat" size="small" :color="color" append-icon="mdi-dots-vertical">{{ accountRole }}</v-btn>
        </template>

        <v-list>
            <v-list-item @click="showConfirmDeleteVesselModal = true" :title="removeButtonTitle">
                <template v-slot:prepend>
                    <v-icon color="red-darken-1" icon="mdi-minus-circle"></v-icon>
                </template>
            </v-list-item>
        </v-list>
    </v-menu>

    <v-dialog v-model="showConfirmDeleteVesselModal" max-width="500px">
        <v-card>
            <v-card-title>{{ confirmationModalTitle }}</v-card-title>
            <v-card-text>{{ confirmationModalBody }}</v-card-text>
            <v-card-actions>
                <v-btn color="grey-darken-1" @click="showConfirmDeleteVesselModal = false">No, Cancel</v-btn>
                <v-btn color="error" @click="deleteVessel">Yes, {{ removeButtonTitle }}</v-btn>
            </v-card-actions>
        </v-card>
    </v-dialog>
</template>

<script lang="ts" setup>
import { AccountVesselRolesV1, AccountVesselV1AccountRoleEnum, VesselTypes, vesselTypeString } from '@geckoal/gecko-api-client'
import type { PropType } from 'vue'
import { computed, ref } from 'vue'
import { useApi } from '../../api'
import { handleApiError, titleCase } from '../../lib/utils'

const showConfirmDeleteVesselModal = ref(false)

const props = defineProps({
    accountId: {
        type: Number,
        required: true
    },
    vesselId: {
        type: Number,
        required: true
    },
    vesselType: {
        type: String as PropType<VesselTypes>,
        required: true
    },
    accountRole: {
        type: String as PropType<AccountVesselRolesV1 | AccountVesselV1AccountRoleEnum>,
        required: true
    }
})

const emit = defineEmits(['deleted-vessel'])

const color = computed(() => {
    if (props.accountRole === AccountVesselRolesV1.Owner || props.accountRole === AccountVesselV1AccountRoleEnum.Owner) return 'green-darken-2'
    if (props.accountRole === AccountVesselRolesV1.Viewer || props.accountRole === AccountVesselV1AccountRoleEnum.Viewer) return 'pink-darken-3'
    if (props.accountRole === AccountVesselRolesV1.Caretaker || props.accountRole === AccountVesselV1AccountRoleEnum.Caretaker) return 'deep-purple-darken-2'
    if (props.accountRole === AccountVesselRolesV1.BlueViewer || props.accountRole === AccountVesselV1AccountRoleEnum.BlueViewer) return 'indigo-darken-1'
    return 'grey'
})

const vesselTypeTitle = computed(() => {
    return titleCase(vesselTypeString(props.vesselType))
})

const removeButtonTitle = computed(() => {
    if (props.accountRole === AccountVesselRolesV1.Owner) return `Remove ${vesselTypeTitle.value}`
    return `Remove Shared ${vesselTypeTitle.value}`
})

const confirmationModalTitle = computed(() => {
    return `${removeButtonTitle.value}?`
})

const confirmationModalBody = computed(() => {
    let text = 'This action is permanent and cannot be recovered.'
    if (props.accountRole !== AccountVesselRolesV1.Owner) text += ' This action will not affect the owning account.'
    return text
})

const deleteVessel = async () => {
    await handleApiError(
        async () => {
            const result = await useApi().removeAccountVesselV1({
                accountId: props.accountId,
                vesselId: props.vesselId
            })

            if (result.deleted) {
                console.log('Deleted account vessel', result.deleted)
                emit('deleted-vessel')
            } else {
                throw new Error('Failed to delete vessel')
            }
        },
        async () => {
            showConfirmDeleteVesselModal.value = false
        }
    )
}
</script>

<style scoped lang="scss"></style>
